<template>
  <div v-if="hasAccess" class="space-y-6 pb-10">
    <page-content>
      <section class="space-y-6">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <dashboard-date-filters
            class="xl:flex-1"
            :quick-ranges="quickRanges"
            :active-range-id="activeQuickRangeId"
            :date-range="dateRange"
            :max-date="maxDate"
            @select-range="applyQuickRange"
            @show-full="showFull"
            @update:date-range="handleDateRangeUpdate"
          />

          <div class="tracking-filters inline-flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 bg-white px-4 py-3 shadow-sm xl:min-w-[700px]">
            <div class="tracking-filter-field tracking-filter-field--wide">
              <t-rich-select
                :key="domainSelectKey"
                class="tracking-filter-control"
                multiple
                :close-on-select="false"
                :options="domainOptions"
                value-attribute="uuid"
                text-attribute="label"
                placeholder="Tous les domaines"
                v-model="selectedDomainUuids"
              >
                <template slot="label" slot-scope="{ options, className }">
                  <span :class="className">
                    {{ domainSelectionLabel }}
                  </span>
                </template>
              </t-rich-select>
            </div>

            <div class="tracking-filter-field">
              <t-select
                class="tracking-filter-control"
                :options="sourceOptions"
                value-attribute="value"
                text-attribute="label"
                v-model="selectedSource"
              />
            </div>

            <div class="tracking-filter-field">
              <t-select
                class="tracking-filter-control"
                :options="typeOptions"
                value-attribute="value"
                text-attribute="label"
                v-model="selectedType"
              />
            </div>

            <employee-activity-export-button
              :date-range="dateRange"
              :selected-domain-uuids="selectedDomainUuids"
            />
          </div>
        </div>

        <div
          v-if="showHistoricalNotice"
          class="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900"
        >
          Les totaux incluent
          <strong>{{ formatNumber(legacyTotals.totalReads) }}</strong>
          lectures historiques non sourcées. Les volumes par source couvrent les lectures tracées depuis le
          <strong>{{ formattedTrackingStartAt }}</strong>.
        </div>

        <div class="content-top-grid">
          <section class="content-space content-space--reads bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="content-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Lectures</h2>
                <button
                  type="button"
                  class="tracking-info-button"
                  title="Suivi mois par mois"
                  aria-label="Ouvrir le suivi mois par mois des lectures"
                  @click="openReadsMonthlyTracking"
                >
                  +
                </button>
              </div>
              <div class="tracking-period-stats">
                <article class="tracking-period-stat tracking-period-stat--all">
                  <span>All sur la période</span>
                  <strong>{{ formatNumber(totalReads) }}</strong>
                </article>
                <article class="tracking-period-stat tracking-period-stat--article">
                  <span>Articles période</span>
                  <strong>{{ formatNumber(totals.articleReads) }}</strong>
                </article>
                <article class="tracking-period-stat tracking-period-stat--faq">
                  <span>FAQ période</span>
                  <strong>{{ formatNumber(totals.faqReads) }}</strong>
                </article>
              </div>
            </header>

            <div class="content-card-body">
              <div class="content-chart-area">
                <div class="tracking-chart-panel">
                  <chart-connection-trend
                    v-if="hasReadSeries"
                    class="h-full w-full"
                    :chart-data="readChartData"
                  />
                  <div v-else class="content-empty-state">
                    Aucune lecture sur cette période.
                  </div>
                </div>
                <div class="content-segmented-control" aria-label="Donnée du graphique lectures">
                  <button
                    v-for="option in readChartOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': readChartMode === option.value }"
                    @click="readChartMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <article class="content-ring-card">
                <div class="content-ring">
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle class="content-ring-bg" cx="60" cy="60" r="48" />
                    <circle
                      class="content-ring-progress"
                      cx="60"
                      cy="60"
                      r="48"
                      :stroke-dasharray="ringCircumference"
                      :stroke-dashoffset="readsRingStrokeOffset"
                    />
                  </svg>
                  <span>{{ formatDecimal(readsRingValue) }}</span>
                </div>
                <p>{{ readsRingLabel }}</p>
                <div class="content-segmented-control content-segmented-control--compact" aria-label="Formule contenus lus">
                  <button
                    v-for="option in readsRingOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': readsRingMode === option.value }"
                    @click="readsRingMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </article>
            </div>
          </section>

          <section class="content-space content-space--searches bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="content-space-header content-space-header--compact">
              <h2 class="text-lg font-semibold text-gray-900">Recherches</h2>
              <article class="tracking-period-stat tracking-period-stat--search">
                <span>Sur la période</span>
                <strong>{{ formatNumber(searchStats.total) }}</strong>
              </article>
            </header>

            <div class="content-search-body">
              <div>
                <h3>Mots sensibles</h3>
                <div class="content-list">
                  <article v-if="sensitiveTerms.length === 0" class="content-list-empty">
                    Aucun mot sensible détecté.
                  </article>
                  <button
                    v-for="term in sensitiveTerms"
                    :key="term.term"
                    type="button"
                    class="content-list-row content-list-row--button"
                    :class="{ 'is-clickable': hasHiddenSensitiveDomains(term) }"
                    :disabled="!hasHiddenSensitiveDomains(term)"
                    @click="openSensitiveDomains(term)"
                  >
                    <div>
                      <strong>{{ term.term }}</strong>
                      <span>{{ formatSensitiveDomains(term.domains) }}</span>
                      <small v-if="hasHiddenSensitiveDomains(term)">Voir tous les domaines</small>
                    </div>
                    <em>{{ formatNumber(term.total) }}</em>
                  </button>
                </div>
              </div>

              <div>
                <h3>Top 10 recherches</h3>
                <div class="content-list">
                  <article v-if="topKeywords.length === 0" class="content-list-empty">
                    Aucune recherche sur cette période.
                  </article>
                  <article v-for="keyword in topKeywords" :key="keyword.query" class="content-list-row">
                    <div>
                      <strong>{{ keyword.query }}</strong>
                    </div>
                    <em>{{ formatNumber(keyword.total) }}</em>
                  </article>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div class="content-secondary-grid">
          <section class="content-space content-space--source bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="content-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Volume par source</h2>
                <button
                  type="button"
                  class="tracking-info-button"
                  title="Suivi mois par mois"
                  @click="openSourcesMonthlyTracking"
                >
                  +
                </button>
              </div>
            </header>
            <div class="content-source-chart">
              <div v-if="hasSourceData" class="content-source-mix">
                <div class="content-source-bars">
                  <chart-read-tracking-bar
                    class="h-full w-full"
                    :height="300"
                    :chart-data="sourceVolumeChartData"
                    :show-percent-in-tooltip="true"
                  />
                </div>
                <div class="content-source-pie">
                  <chart-read-tracking-doughnut
                    class="h-full w-full"
                    :height="260"
                    :chart-data="sourceVolumeChartData"
                    :show-legend="false"
                    :show-percent-in-tooltip="true"
                  />
                </div>
              </div>
              <div v-else class="content-empty-state">
                Aucune lecture tracée sur cette période.
              </div>
            </div>
          </section>

          <section class="content-space content-space--banners bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="content-space-header content-space-header--compact">
              <h2 class="text-lg font-semibold text-gray-900">Utilisation des bandeaux</h2>
            </header>
            <div class="content-banner-table-wrapper">
              <table class="content-simple-table">
                <thead>
                  <tr>
                    <th>Bandeau</th>
                    <th class="is-right">Lectures</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in bannerSourceVolumes" :key="row.source">
                    <td>{{ formatSourceLabel(row.source) }}</td>
                    <td class="is-right">{{ formatNumber(row.count) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="content-space content-space--messaging bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="content-space-header content-space-header--compact">
              <h2 class="text-lg font-semibold text-gray-900">Messagerie</h2>
              <article class="tracking-period-stat tracking-period-stat--message">
                <span>Sur la période</span>
                <strong>{{ formatNumber(messagingStats.started) }}</strong>
              </article>
            </header>
            <div class="content-message-body">
              <div class="content-message-entry">
                <button type="button" class="content-link-button" @click="openDomainsWithoutInterlocutor">
                  Domaines sans interlocuteur
                </button>
                <p>{{ formatNumber(domainsWithoutInterlocutor.length) }} domaine(s) concerné(s)</p>
              </div>
              <div class="content-message-entry">
                <button type="button" class="content-link-button" @click="openDomainsWithInterlocutorWithoutConversation">
                  Domaines avec interlocuteur sans conversation sur la période
                </button>
                <p>{{ formatNumber(domainsWithInterlocutorWithoutConversation.length) }} domaine(s) concerné(s)</p>
              </div>
            </div>
          </section>
        </div>

        <section class="rounded-2xl border border-blue-200 bg-white shadow-sm">
          <header class="border-b border-gray-200 px-5 py-4">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Détail client</h2>
                <p class="mt-1 text-sm text-gray-600">
                  Totaux agrégés par domaine sur la période. La ligne total moyenne les lectures par salarié.
                </p>
              </div>
              <p v-if="isLoading" class="text-sm text-gray-600">Chargement des données...</p>
            </div>
          </header>
          <div class="px-2 py-4">
            <m-v-p-table
              v-if="gridOptions"
              :options="gridOptions"
              :grid-data="gridData"
              status-bar-type="total"
              @gridReady="onGridReady"
            />
          </div>
        </section>

        <div
          v-if="isReadsMonthlyTrackingOpen"
          class="tracking-modal-overlay"
          @click.self="closeReadsMonthlyTracking"
        >
          <div class="tracking-modal-card tracking-modal-card--wide">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Suivi mois par mois</h3>
                <p class="tracking-modal-subtitle">Lectures, contenus lus par salarié et recherches.</p>
              </div>
              <div class="tracking-modal-domain-filter">
                <label>Client</label>
                <t-rich-select
                  :key="`${domainSelectKey}::reads-monthly`"
                  class="tracking-filter-control"
                  multiple
                  :close-on-select="false"
                  :options="domainOptions"
                  value-attribute="uuid"
                  text-attribute="label"
                  placeholder="Tous les domaines"
                  v-model="selectedDomainUuids"
                >
                  <template slot="label" slot-scope="{ options, className }">
                    <span :class="className">
                      {{ domainSelectionLabel }}
                    </span>
                  </template>
                </t-rich-select>
              </div>
              <button type="button" class="tracking-modal-close" @click="closeReadsMonthlyTracking">×</button>
            </header>

            <div class="tracking-modal-table-wrapper tracking-monthly-table-wrapper">
              <table class="tracking-modal-table tracking-monthly-table">
                <thead>
                  <tr>
                    <th>
                      <select v-model.number="selectedTrackingYear" class="tracking-year-select">
                        <option v-for="year in trackingYearOptions" :key="year" :value="year">{{ year }}</option>
                      </select>
                    </th>
                    <th v-for="month in monthlyTrackingRows" :key="month.key" class="is-right">{{ month.label }}</th>
                  </tr>
                </thead>
                <tbody v-if="!isMonthlyTrackingLoading">
                  <tr v-for="row in readMonthlyTableRows" :key="row.key">
                    <th>{{ row.label }}</th>
                    <td v-for="month in monthlyTrackingRows" :key="`${row.key}-${month.key}`" class="is-right">
                      {{ formatMonthlyValue(month[row.key], row.format) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="isMonthlyTrackingLoading" class="tracking-monthly-loader" aria-live="polite">
                <span class="tracking-monthly-spinner" />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="isSourcesMonthlyTrackingOpen"
          class="tracking-modal-overlay"
          @click.self="closeSourcesMonthlyTracking"
        >
          <div class="tracking-modal-card tracking-modal-card--wide">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Suivi des sources</h3>
                <p class="tracking-modal-subtitle">Volumes de lecture par source sur l'année sélectionnée.</p>
              </div>
              <div class="tracking-modal-domain-filter">
                <label>Client</label>
                <t-rich-select
                  :key="`${domainSelectKey}::sources-monthly`"
                  class="tracking-filter-control"
                  multiple
                  :close-on-select="false"
                  :options="domainOptions"
                  value-attribute="uuid"
                  text-attribute="label"
                  placeholder="Tous les domaines"
                  v-model="selectedDomainUuids"
                >
                  <template slot="label" slot-scope="{ options, className }">
                    <span :class="className">
                      {{ domainSelectionLabel }}
                    </span>
                  </template>
                </t-rich-select>
              </div>
              <button type="button" class="tracking-modal-close" @click="closeSourcesMonthlyTracking">×</button>
            </header>

            <div class="tracking-modal-table-wrapper tracking-monthly-table-wrapper">
              <table class="tracking-modal-table tracking-monthly-table">
                <thead>
                  <tr>
                    <th>
                      <select v-model.number="selectedTrackingYear" class="tracking-year-select">
                        <option v-for="year in trackingYearOptions" :key="year" :value="year">{{ year }}</option>
                      </select>
                    </th>
                    <th v-for="month in monthlyTrackingRows" :key="month.key" class="is-right">{{ month.label }}</th>
                  </tr>
                </thead>
                <tbody v-if="!isMonthlyTrackingLoading">
                  <tr v-for="row in sourceMonthlyTableRows" :key="row.key">
                    <th>{{ row.label }}</th>
                    <td v-for="month in monthlyTrackingRows" :key="`${row.key}-${month.key}`" class="is-right">
                      {{ formatNumber(month[row.key]) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="isMonthlyTrackingLoading" class="tracking-monthly-loader" aria-live="polite">
                <span class="tracking-monthly-spinner" />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="isDomainsWithoutInterlocutorOpen"
          class="tracking-modal-overlay"
          @click.self="closeDomainsWithoutInterlocutor"
        >
          <div class="tracking-modal-card">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Domaines sans interlocuteur</h3>
                <p class="tracking-modal-subtitle">Aucun utilisateur n'est configuré comme interlocuteur de messagerie.</p>
              </div>
              <button type="button" class="tracking-modal-close" @click="closeDomainsWithoutInterlocutor">×</button>
            </header>
            <div class="tracking-modal-table-wrapper">
              <table class="tracking-modal-table">
                <thead>
                  <tr><th>Domaine</th></tr>
                </thead>
                <tbody>
                  <tr v-if="domainsWithoutInterlocutor.length === 0">
                    <td>Tous les domaines filtrés ont un interlocuteur.</td>
                  </tr>
                  <tr v-for="domain in domainsWithoutInterlocutor" :key="domain.domain_uuid">
                    <td>{{ domain.domain_label }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div
          v-if="isDomainsWithInterlocutorWithoutConversationOpen"
          class="tracking-modal-overlay"
          @click.self="closeDomainsWithInterlocutorWithoutConversation"
        >
          <div class="tracking-modal-card">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Domaines sans conversation</h3>
                <p class="tracking-modal-subtitle">
                  Domaines avec interlocuteur configuré, mais sans conversation sur la période filtrée.
                </p>
              </div>
              <button type="button" class="tracking-modal-close" @click="closeDomainsWithInterlocutorWithoutConversation">×</button>
            </header>
            <div class="tracking-modal-table-wrapper">
              <table class="tracking-modal-table">
                <thead>
                  <tr><th>Domaine</th></tr>
                </thead>
                <tbody>
                  <tr v-if="domainsWithInterlocutorWithoutConversation.length === 0">
                    <td>Tous les domaines avec interlocuteur ont au moins une conversation sur la période.</td>
                  </tr>
                  <tr v-for="domain in domainsWithInterlocutorWithoutConversation" :key="domain.domain_uuid">
                    <td>{{ domain.domain_label }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div
          v-if="selectedSensitiveTerm"
          class="tracking-modal-overlay"
          @click.self="closeSensitiveDomains"
        >
          <div class="tracking-modal-card">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Domaines concernés</h3>
                <p class="tracking-modal-subtitle">
                  Mot sensible : {{ selectedSensitiveTerm.term }}
                </p>
              </div>
              <button type="button" class="tracking-modal-close" @click="closeSensitiveDomains">×</button>
            </header>
            <div class="tracking-modal-table-wrapper">
              <table class="tracking-modal-table">
                <thead>
                  <tr>
                    <th>Domaine</th>
                    <th class="is-right">Occurrences</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="domain in selectedSensitiveDomains" :key="domain.domain_uuid">
                    <td>{{ domain.domain_label }}</td>
                    <td class="is-right">{{ formatNumber(domain.total) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </page-content>
  </div>
  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-600">
      Vous n'avez pas accès à cette section.
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { http } from '@/services/api.service'
import { format, parseISO, subDays, subMonths, subWeeks } from 'date-fns'
import fr from 'date-fns/locale/fr'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import DashboardDateFilters from '@/components/dashboard/DashboardDateFilters.vue'
import EmployeeActivityExportButton from '@/components/dashboard/EmployeeActivityExportButton.vue'
import ChartConnectionTrend from '@/components/charts/ChartConnectionTrend.vue'
import ChartReadTrackingBar from '@/components/charts/ChartReadTrackingBar.vue'
import ChartReadTrackingDoughnut from '@/components/charts/ChartReadTrackingDoughnut.vue'
import { DASHBOARD_QUICK_RANGES } from './state'
import { getFullTrackingDateRange } from './domainPeriod'
import { fetchTrackingDomains } from './trackingDomains'
import { formatTrackingDomainSelectionLabel, getTrackingQueryState, normalizeTrackingDomainUuids, syncTrackingQueryFromVm } from './trackingQuery'

const DATE_FORMAT = 'yyyy-MM-dd'
const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
const SOURCE_LABELS = {
  notification: 'Notification',
  homepage_notification_rebound: 'Accueil rebond',
  homepage_app_open: 'Accueil ouverture',
  homepage: 'Accueil',
  search: 'Recherche',
  suggestion: 'Suggestions',
  internal_navigation: 'Navigation',
  banner_elected_selection: 'Sélection des élus',
  banner_top_articles: 'Articles les plus lus',
  banner_top_faq: 'FAQ les plus lues'
}
const SOURCE_COLORS = {
  notification: '#2563EB',
  homepage_notification_rebound: '#0F766E',
  homepage_app_open: '#0891B2',
  homepage: '#64748B',
  search: '#D97706',
  suggestion: '#7C3AED',
  internal_navigation: '#DC2626',
  banner_elected_selection: '#1D4ED8',
  banner_top_articles: '#EA580C',
  banner_top_faq: '#4338CA'
}
const SOURCE_ORDER = Object.keys(SOURCE_LABELS)
const SOURCE_FILTER_OPTIONS = [
  { value: '', label: 'Toutes les sources' },
  { value: 'notification', label: 'Notification' },
  { value: 'homepage_app_open', label: 'Accueil (ouverture app)' },
  { value: 'homepage_notification_rebound', label: 'Accueil (via notif)' },
  { value: 'search', label: 'Recherche' },
  { value: 'suggestion', label: 'Suggestions' },
  { value: 'internal_navigation', label: 'Navigation' }
]
const BANNER_SOURCE_ORDER = ['banner_elected_selection', 'banner_top_articles', 'banner_top_faq']
const CONTENT_SESSION_SOURCES = ['homepage', 'homepage_app_open', 'homepage_notification_rebound', 'search', 'suggestion', 'internal_navigation']

function numericComparator (valueA, valueB) {
  return Number(valueA || 0) - Number(valueB || 0)
}

function toIsoDate (date) {
  return format(date, DATE_FORMAT)
}

function normalizeTrackingStartAt (value) {
  if (!value || typeof value !== 'string') {
    return null
  }

  try {
    return parseISO(value.replace(' ', 'T'))
  } catch (error) {
    return null
  }
}

function createDefaultTotals () {
  return {
    articleReads: 0,
    faqReads: 0,
    totalReads: 0,
    downloads: 0,
    readsPerDownload: 0,
    readsPerDownloadAverage: 0
  }
}

export default {
  name: 'app-dashboard-tracking-reads',
  components: {
    PageContent,
    MVPTable,
    DashboardDateFilters,
    EmployeeActivityExportButton,
    ChartConnectionTrend,
    ChartReadTrackingBar,
    ChartReadTrackingDoughnut
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      const hasAccess = await vm.ensureAccess()
      if (!hasAccess) {
        vm.$router.replace('/app/dashboard')
      }
    })
  },
  data () {
    const today = new Date()
    const defaultEnd = toIsoDate(today)
    const defaultStart = toIsoDate(subDays(today, 29))
    const trackingState = getTrackingQueryState(this.$route.query, {
      activeQuickRangeId: 'month-1',
      dateRange: [defaultStart, defaultEnd],
      selectedDomainUuids: []
    })

    return {
      quickRanges: DASHBOARD_QUICK_RANGES.map(range => ({ ...range })),
      activeQuickRangeId: trackingState.activeQuickRangeId,
      dateRange: trackingState.dateRange,
      maxDate: defaultEnd,
      domainOptions: [],
      selectedDomainUuids: trackingState.selectedDomainUuids,
      selectedSource: '',
      selectedType: '',
      selectedTrackingYear: today.getFullYear(),
      readChartMode: 'allReads',
      readsRingMode: 'downloads',
      totals: createDefaultTotals(),
      legacyTotals: { articleReads: 0, faqReads: 0, totalReads: 0 },
      eventTotals: { articleReads: 0, faqReads: 0, totalReads: 0 },
      searchStats: { total: 0, topKeywords: [], sensitiveTerms: [] },
      messagingStats: {
        started: 0,
        domainsWithoutInterlocutor: [],
        domainsWithInterlocutorWithoutConversation: []
      },
      sourceVolumes: [],
      sourceSessionVolumes: [],
      dailySeries: [],
      monthlySeries: [],
      tableTotals: null,
      gridOptions: null,
      gridData: [],
      gridApi: null,
      totalRowCount: 0,
      isLoading: false,
      isMonthlyTrackingLoading: false,
      isReadsMonthlyTrackingOpen: false,
      isSourcesMonthlyTrackingOpen: false,
      isDomainsWithoutInterlocutorOpen: false,
      isDomainsWithInterlocutorWithoutConversationOpen: false,
      selectedSensitiveTerm: null,
      trackingStartAt: null,
      usesHistoricalTotals: false,
      activeStatsRequestKey: null,
      activeMonthlyRequestKey: null,
      statsRequestSequence: 0,
      monthlyRequestSequence: 0,
      debouncedLoadStats: null,
      debouncedLoadMonthlyTracking: null,
      isInitializingTracking: true
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.includes('admin') || this.roles.includes('community-manager')
    },
    hasDomainFilter () {
      return normalizeTrackingDomainUuids(this.selectedDomainUuids).length > 0
    },
    domainSelectionLabel () {
      return formatTrackingDomainSelectionLabel(this.selectedDomainUuids, this.domainOptions)
    },
    domainSelectKey () {
      return `${this.$route.path}::${this.domainOptions.length}`
    },
    sourceOptions () {
      return SOURCE_FILTER_OPTIONS
    },
    typeOptions () {
      return [
        { value: '', label: 'Tous les types' },
        { value: 'article', label: 'Article' },
        { value: 'faq', label: 'FAQ' }
      ]
    },
    readChartOptions () {
      return [
        { value: 'allReads', label: 'Lecture all' },
        { value: 'articleReads', label: 'Articles' },
        { value: 'faqReads', label: 'FAQ' }
      ]
    },
    readsRingOptions () {
      const options = [
        { value: 'downloads', label: 'DL' },
        { value: 'average', label: 'Moyenne' }
      ]

      return this.hasDomainFilter
        ? options.filter(option => option.value !== 'average')
        : options
    },
    totalReads () {
      return Number(this.totals.totalReads || 0) || (Number(this.totals.articleReads || 0) + Number(this.totals.faqReads || 0))
    },
    hasLegacyTotals () {
      return Number(this.legacyTotals.totalReads || 0) > 0
    },
    normalizedDailySeries () {
      const rows = Array.isArray(this.dailySeries) ? this.dailySeries : []
      return rows.map(row => ({
        date: row?.date || '',
        allReads: Number(row?.allReads || 0),
        articleReads: Number(row?.articleReads || 0),
        faqReads: Number(row?.faqReads || 0)
      }))
    },
    hasReadSeries () {
      return this.normalizedDailySeries.some(row => row.allReads > 0 || row.articleReads > 0 || row.faqReads > 0)
    },
    readChartData () {
      const config = this.readChartConfig
      return {
        labels: this.normalizedDailySeries.map(item => this.formatDateLabel(item.date)),
        datasets: [
          {
            label: config.label,
            data: this.normalizedDailySeries.map(item => Number(item[config.key] || 0)),
            borderColor: config.color,
            backgroundColor: this.hexToRgba(config.color, 0.12),
            borderWidth: this.normalizedDailySeries.length > 31 ? 0.75 : 1,
            pointBackgroundColor: config.color,
            pointRadius: this.normalizedDailySeries.length > 31 ? 0 : (this.normalizedDailySeries.length > 14 ? 1 : 1.5),
            pointHoverRadius: 2,
            pointHitRadius: 8,
            fill: false
          }
        ]
      }
    },
    readChartConfig () {
      if (this.readChartMode === 'articleReads') {
        return { key: 'articleReads', label: 'Lectures articles', color: '#7c3aed' }
      }

      if (this.readChartMode === 'faqReads') {
        return { key: 'faqReads', label: 'Lectures FAQ', color: '#0f766e' }
      }

      return { key: 'allReads', label: 'Lectures all', color: '#2563eb' }
    },
    ringCircumference () {
      return 2 * Math.PI * 48
    },
    readsRingValue () {
      return this.readsRingMode === 'average'
        ? Number(this.totals.readsPerDownloadAverage || 0)
        : Number(this.totals.readsPerDownload || 0)
    },
    readsRingLabel () {
      return this.readsRingMode === 'average'
        ? 'Moyenne des clients actifs'
        : 'Contenus lus par salarié'
    },
    readsRingStrokeOffset () {
      const progress = Math.min(100, Math.max(0, this.readsRingValue * 10))
      return this.ringCircumference * (1 - (progress / 100))
    },
    sourceVolumeMap () {
      return (this.sourceVolumes || []).reduce((acc, item) => {
        if (item?.source) {
          acc[item.source] = Number(item?.count || 0)
        }
        return acc
      }, {})
    },
    sourceSessionVolumeMap () {
      return (this.sourceSessionVolumes || []).reduce((acc, item) => {
        const source = item?.source
        if (!source) return acc
        const key = `${source}::${item?.session_entry || 'null'}`
        acc[key] = Number(item?.count || 0)
        return acc
      }, {})
    },
    viaNotificationSessionCount () {
      const sessionCount = this.sumSessionSourceCounts(CONTENT_SESSION_SOURCES, 'notification')
      const legacyFallbackCount = this.getSessionSourceCount('homepage_notification_rebound', null)
      return sessionCount + legacyFallbackCount
    },
    homepageTotalCount () {
      return Number(this.sourceVolumeMap.homepage || 0) +
        Number(this.sourceVolumeMap.homepage_app_open || 0) +
        Number(this.sourceVolumeMap.homepage_notification_rebound || 0)
    },
    homepageAppCount () {
      return Math.max(0, this.homepageTotalCount - this.viaNotificationSessionCount)
    },
    sourceVolumeRows () {
      return [
        { source: 'notification', label: 'Notification', count: Number(this.sourceVolumeMap.notification || 0), color: SOURCE_COLORS.notification },
        { source: 'homepage_app_open', label: 'Accueil ouverture', count: this.homepageAppCount, color: SOURCE_COLORS.homepage_app_open },
        { source: 'homepage_notification_rebound', label: 'Accueil rebond', count: this.viaNotificationSessionCount, color: SOURCE_COLORS.homepage_notification_rebound },
        { source: 'search', label: 'Recherche', count: this.getNonNotificationSessionSourceCount('search'), color: SOURCE_COLORS.search },
        { source: 'suggestion', label: 'Suggestions', count: this.getNonNotificationSessionSourceCount('suggestion'), color: SOURCE_COLORS.suggestion },
        { source: 'internal_navigation', label: 'Navigation', count: this.getNonNotificationSessionSourceCount('internal_navigation'), color: SOURCE_COLORS.internal_navigation },
        { source: 'banner_elected_selection', label: 'Sélection des élus', count: Number(this.sourceVolumeMap.banner_elected_selection || 0), color: SOURCE_COLORS.banner_elected_selection },
        { source: 'banner_top_articles', label: 'Articles les plus lus', count: Number(this.sourceVolumeMap.banner_top_articles || 0), color: SOURCE_COLORS.banner_top_articles },
        { source: 'banner_top_faq', label: 'FAQ les plus lues', count: Number(this.sourceVolumeMap.banner_top_faq || 0), color: SOURCE_COLORS.banner_top_faq }
      ]
    },
    hasSourceData () {
      return this.sourceVolumeRows.some(row => row.count > 0)
    },
    sourceVolumeChartData () {
      const total = this.sourceVolumeRows.reduce((sum, row) => sum + Number(row.count || 0), 0)
      return {
        labels: this.sourceVolumeRows.map(row => row.label),
        tooltipTotal: total,
        datasets: [
          {
            label: 'Lectures',
            backgroundColor: this.sourceVolumeRows.map(row => row.color || '#94A3B8'),
            data: this.sourceVolumeRows.map(row => row.count)
          }
        ]
      }
    },
    bannerSourceVolumes () {
      return BANNER_SOURCE_ORDER.map(source => ({
        source,
        count: Number(this.sourceVolumeMap[source] || 0)
      }))
    },
    sensitiveTerms () {
      return Array.isArray(this.searchStats.sensitiveTerms) ? this.searchStats.sensitiveTerms : []
    },
    selectedSensitiveDomains () {
      return Array.isArray(this.selectedSensitiveTerm?.domains)
        ? this.selectedSensitiveTerm.domains
        : []
    },
    topKeywords () {
      return Array.isArray(this.searchStats.topKeywords) ? this.searchStats.topKeywords : []
    },
    domainsWithoutInterlocutor () {
      return Array.isArray(this.messagingStats.domainsWithoutInterlocutor)
        ? this.messagingStats.domainsWithoutInterlocutor
        : []
    },
    domainsWithInterlocutorWithoutConversation () {
      return Array.isArray(this.messagingStats.domainsWithInterlocutorWithoutConversation)
        ? this.messagingStats.domainsWithInterlocutorWithoutConversation
        : []
    },
    trackingYearOptions () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2020; year--) {
        years.push(year)
      }
      return years
    },
    monthlyTrackingRows () {
      const rows = Array.isArray(this.monthlySeries) ? this.monthlySeries : []
      return MONTH_LABELS.map((label, index) => {
        const monthNumber = index + 1
        const row = rows.find(item => Number(item.month) === monthNumber) || {}
        return {
          key: monthNumber,
          label,
          allReads: Number(row.allReads || 0),
          articleReads: Number(row.articleReads || 0),
          faqReads: Number(row.faqReads || 0),
          readsPerDownload: Number(row.readsPerDownload || 0),
          readsPerDownloadAverage: Number(row.readsPerDownloadAverage || 0),
          searches: Number(row.searches || 0),
          sourceNotification: Number(row.sourceNotification || 0),
          sourceHomepageAppOpen: Number(row.sourceHomepageAppOpen || 0),
          sourceHomepageRebound: Number(row.sourceHomepageRebound || 0),
          sourceSearch: Number(row.sourceSearch || 0),
          sourceSuggestion: Number(row.sourceSuggestion || 0),
          sourceNavigation: Number(row.sourceNavigation || 0),
          sourceBannerElected: Number(row.sourceBannerElected || 0),
          sourceBannerTopArticles: Number(row.sourceBannerTopArticles || 0),
          sourceBannerTopFaq: Number(row.sourceBannerTopFaq || 0)
        }
      })
    },
    readMonthlyTableRows () {
      const rows = [
        { key: 'allReads', label: 'Lecture all', format: 'number', types: ['article', 'faq'] },
        { key: 'articleReads', label: 'Lectures articles', format: 'number', types: ['article'] },
        { key: 'faqReads', label: 'Lectures FAQ', format: 'number', types: ['faq'] },
        { key: 'readsPerDownload', label: 'Contenus lus par salariés DL', format: 'decimal', types: ['article', 'faq'] },
        { key: 'searches', label: 'Nombre de recherche', format: 'number', types: ['article', 'faq'] }
      ]

      if (!this.hasDomainFilter) {
        rows.splice(4, 0, { key: 'readsPerDownloadAverage', label: 'Contenus lus par salariés moyenne', format: 'decimal', types: ['article', 'faq'] })
      }

      return this.selectedType
        ? rows.filter(row => row.types.includes(this.selectedType))
        : rows
    },
    sourceMonthlyTableRows () {
      const rows = [
        { key: 'sourceNotification', label: 'Total lectures notif', sources: ['notification'] },
        { key: 'sourceHomepageAppOpen', label: 'Total lectures accueil ouverture', sources: ['homepage_app_open'] },
        { key: 'sourceHomepageRebound', label: 'Total lectures accueil rebond', sources: ['homepage_notification_rebound'] },
        { key: 'sourceSearch', label: 'Total lectures recherche', sources: ['search'] },
        { key: 'sourceSuggestion', label: 'Total lectures suggestions', sources: ['suggestion'] },
        { key: 'sourceNavigation', label: 'Total lectures navigation', sources: ['internal_navigation'] },
        { key: 'sourceBannerElected', label: 'Total lectures sélection des élus', sources: ['banner_elected_selection'] },
        { key: 'sourceBannerTopArticles', label: 'Total lectures articles les plus lus', sources: ['banner_top_articles'] },
        { key: 'sourceBannerTopFaq', label: 'Total lectures FAQ les plus lues', sources: ['banner_top_faq'] }
      ]

      return this.selectedSource
        ? rows.filter(row => row.sources.includes(this.selectedSource))
        : rows
    },
    showHistoricalNotice () {
      if (!this.usesHistoricalTotals || !this.trackingStartAt || !Array.isArray(this.dateRange) || this.dateRange.length !== 2) {
        return false
      }

      try {
        return parseISO(this.dateRange[0]).getTime() < this.trackingStartAt.getTime()
      } catch (error) {
        return false
      }
    },
    formattedTrackingStartAt () {
      return this.trackingStartAt ? format(this.trackingStartAt, 'dd/MM/yyyy', { locale: fr }) : ''
    }
  },
  watch: {
    selectedDomainUuids: {
      handler () {
        if (this.activeQuickRangeId === 'full') {
          this.setFullDateRange()
        }
        syncTrackingQueryFromVm(this)
        this.syncReadsRingMode()
        if (this.isInitializingTracking) {
          return
        }
        this.scheduleStatsReload()
        if (this.isReadsMonthlyTrackingOpen || this.isSourcesMonthlyTrackingOpen) {
          this.scheduleMonthlyTrackingReload()
        }
      },
      deep: true
    },
    selectedSource () {
      if (this.isInitializingTracking) {
        return
      }
      this.scheduleStatsReload()
      if (this.isReadsMonthlyTrackingOpen || this.isSourcesMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    },
    selectedType () {
      if (this.isInitializingTracking) {
        return
      }
      this.scheduleStatsReload()
      if (this.isReadsMonthlyTrackingOpen || this.isSourcesMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    },
    selectedTrackingYear () {
      if (!this.isInitializingTracking && (this.isReadsMonthlyTrackingOpen || this.isSourcesMonthlyTrackingOpen)) {
        this.scheduleMonthlyTrackingReload()
      }
    }
  },
  async created () {
    this.initializeGridOptions()
    this.debouncedLoadStats = debounce(this.loadStats, 400)
    this.debouncedLoadMonthlyTracking = debounce(this.loadMonthlyTracking, 400)

    const hasAccess = await this.ensureAccess()
    if (!hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    const waitForDomains = this.activeQuickRangeId === 'full'
    const domainsPromise = this.fetchDomains()
    if (waitForDomains) {
      await domainsPromise
      this.setFullDateRange()
    }
    this.syncReadsRingMode()
    syncTrackingQueryFromVm(this)
    this.isInitializingTracking = false
    this.loadStats()
    if (this.isReadsMonthlyTrackingOpen || this.isSourcesMonthlyTrackingOpen) {
      this.loadMonthlyTracking()
    }
    if (!waitForDomains) {
      await domainsPromise
    }
  },
  beforeDestroy () {
    if (this.debouncedLoadStats) {
      this.debouncedLoadStats.cancel()
    }
    if (this.debouncedLoadMonthlyTracking) {
      this.debouncedLoadMonthlyTracking.cancel()
    }
    this.statsRequestSequence += 1
    this.monthlyRequestSequence += 1
  },
  methods: {
    async ensureAccess () {
      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        try {
          await this.$store.dispatch('FETCH_ROLES')
        } catch (error) {
          return false
        }
      }

      return this.hasAccess
    },
    initializeGridOptions () {
      const sourceColumns = SOURCE_ORDER.map(source => ({
        headerName: this.formatSourceLabel(source),
        field: source,
        sortable: true,
        minWidth: source.startsWith('banner_') ? 190 : 150,
        comparator: numericComparator,
        valueFormatter: params => this.formatNumber(params.value)
      }))

      this.gridOptions = {
        rowModelType: 'clientSide',
        suppressMultiSort: true,
        context: { componentParent: this },
        pinnedBottomRowData: [],
        getRowClass: params => params?.node?.rowPinned ? 'tracking-total-row' : '',
        defaultColDef: {
          resizable: true,
          sortable: true,
          floatingFilter: false,
          sortingOrder: ['asc', 'desc']
        },
        columnDefs: [
          { headerName: 'Domaine', field: 'domain_label', sortable: true, minWidth: 210, pinned: 'left' },
          { headerName: 'Total', field: 'total_reads', sortable: true, minWidth: 110, comparator: numericComparator, valueFormatter: params => this.formatNumber(params.value) },
          { headerName: 'Historiques', field: 'total_reads_legacy', sortable: true, minWidth: 130, comparator: numericComparator, valueFormatter: params => this.formatNumber(params.value) },
          { headerName: 'Tracées', field: 'total_reads_event', sortable: true, minWidth: 120, comparator: numericComparator, valueFormatter: params => this.formatNumber(params.value) },
          { headerName: 'Articles', field: 'article_reads', sortable: true, minWidth: 120, comparator: numericComparator, valueFormatter: params => this.formatNumber(params.value) },
          { headerName: 'FAQ', field: 'faq_reads', sortable: true, minWidth: 100, comparator: numericComparator, valueFormatter: params => this.formatNumber(params.value) },
          { headerName: 'DL', field: 'downloads', sortable: true, minWidth: 100, comparator: numericComparator, valueFormatter: params => this.formatNumber(params.value) },
          { headerName: 'Lectures / salarié', field: 'reads_per_download', sortable: true, minWidth: 160, comparator: numericComparator, valueFormatter: params => this.formatDecimal(params.value) },
          ...sourceColumns
        ]
      }
    },
    async fetchDomains () {
      try {
        const domains = await fetchTrackingDomains()
        this.domainOptions = domains
        const allowedUuids = new Set(domains.map(domain => domain.uuid))
        const currentSelectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
        const nextSelectedDomainUuids = currentSelectedDomainUuids
          .filter(uuid => allowedUuids.has(uuid))

        if (nextSelectedDomainUuids.join('|') !== currentSelectedDomainUuids.join('|')) {
          this.selectedDomainUuids = nextSelectedDomainUuids
        }
      } catch (error) {
        this.domainOptions = []
      }
    },
    scheduleStatsReload () {
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats()
        return
      }
      this.loadStats()
    },
    scheduleMonthlyTrackingReload () {
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking()
        return
      }
      this.loadMonthlyTracking()
    },
    loadStats () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats.cancel()
      }

      const params = this.buildRequestParams()
      const requestKey = JSON.stringify(params)
      if (this.activeStatsRequestKey === requestKey) {
        return
      }

      const requestSequence = this.statsRequestSequence + 1
      this.statsRequestSequence = requestSequence
      this.activeStatsRequestKey = requestKey
      this.isLoading = true

      http.get('/dashboard/read-tracking', { params })
        .then(({ data }) => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }
          this.applyResponseData(data, true)
        })
        .catch(() => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }
          this.resetStats()
          this.$buefy.toast.open({
            message: 'Impossible de charger les statistiques de lectures.',
            type: 'is-danger'
          })
        })
        .finally(() => {
          if (requestSequence === this.statsRequestSequence) {
            this.isLoading = false
            this.activeStatsRequestKey = null
          }
        })
    },
    loadMonthlyTracking () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }

      const params = {
        ...this.buildRequestParams(),
        monthlyOnly: 1
      }
      const requestKey = JSON.stringify(params)
      if (this.activeMonthlyRequestKey === requestKey) {
        return
      }

      const requestSequence = this.monthlyRequestSequence + 1
      this.monthlyRequestSequence = requestSequence
      this.activeMonthlyRequestKey = requestKey
      this.isMonthlyTrackingLoading = true

      http.get('/dashboard/read-tracking', { params })
        .then(({ data }) => {
          if (requestSequence !== this.monthlyRequestSequence) {
            return
          }
          this.monthlySeries = Array.isArray(data?.monthlySeries) ? data.monthlySeries : []
        })
        .catch(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.monthlySeries = []
          }
        })
        .finally(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.isMonthlyTrackingLoading = false
            this.activeMonthlyRequestKey = null
          }
        })
    },
    applyResponseData (data, includeGrid) {
      this.totals = {
        ...createDefaultTotals(),
        ...Object.keys(createDefaultTotals()).reduce((acc, key) => {
          acc[key] = Number(data?.totals?.[key] || 0)
          return acc
        }, {})
      }
      this.legacyTotals = {
        articleReads: Number(data?.legacyTotals?.articleReads || 0),
        faqReads: Number(data?.legacyTotals?.faqReads || 0),
        totalReads: Number(data?.legacyTotals?.totalReads || 0)
      }
      this.eventTotals = {
        articleReads: Number(data?.eventTotals?.articleReads || 0),
        faqReads: Number(data?.eventTotals?.faqReads || 0),
        totalReads: Number(data?.eventTotals?.totalReads || 0)
      }
      this.searchStats = {
        total: Number(data?.searches?.total || 0),
        topKeywords: Array.isArray(data?.searches?.topKeywords) ? data.searches.topKeywords : [],
        sensitiveTerms: Array.isArray(data?.searches?.sensitiveTerms) ? data.searches.sensitiveTerms : []
      }
      this.messagingStats = {
        started: Number(data?.messaging?.started || 0),
        domainsWithoutInterlocutor: Array.isArray(data?.messaging?.domainsWithoutInterlocutor) ? data.messaging.domainsWithoutInterlocutor : [],
        domainsWithInterlocutorWithoutConversation: Array.isArray(data?.messaging?.domainsWithInterlocutorWithoutConversation) ? data.messaging.domainsWithInterlocutorWithoutConversation : []
      }
      this.sourceVolumes = Array.isArray(data?.sourceVolumes) ? data.sourceVolumes : []
      this.sourceSessionVolumes = Array.isArray(data?.sourceSessionVolumes) ? data.sourceSessionVolumes : []
      this.dailySeries = Array.isArray(data?.dailySeries) ? data.dailySeries : []
      this.tableTotals = data?.tableTotals || null
      this.trackingStartAt = normalizeTrackingStartAt(data?.meta?.trackingStartAt)
      this.usesHistoricalTotals = Boolean(data?.meta?.usesHistoricalTotals)

      if (includeGrid) {
        this.gridData = Array.isArray(data?.domains) ? data.domains : []
        this.totalRowCount = this.gridData.length
        this.updatePinnedTotals()
        if (this.gridApi) {
          this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
        }
      }
    },
    resetStats () {
      this.totals = createDefaultTotals()
      this.legacyTotals = { articleReads: 0, faqReads: 0, totalReads: 0 }
      this.eventTotals = { articleReads: 0, faqReads: 0, totalReads: 0 }
      this.searchStats = { total: 0, topKeywords: [], sensitiveTerms: [] }
      this.messagingStats = {
        started: 0,
        domainsWithoutInterlocutor: [],
        domainsWithInterlocutorWithoutConversation: []
      }
      this.sourceVolumes = []
      this.sourceSessionVolumes = []
      this.dailySeries = []
      this.tableTotals = null
      this.gridData = []
      this.totalRowCount = 0
      this.updatePinnedTotals()
    },
    buildRequestParams () {
      const params = { trackingYear: this.selectedTrackingYear }
      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.dateStart = this.dateRange[0]
        params.dateEnd = this.dateRange[1]
      }
      const selectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
      if (selectedDomainUuids.length > 0) {
        params.domains = selectedDomainUuids
      }
      if (this.selectedSource) {
        params.sourceGroup = this.selectedSource
      }
      if (this.selectedType) {
        params.type = this.selectedType
      }
      return params
    },
    applyQuickRange (range) {
      if (!range) return
      this.activeQuickRangeId = range.id
      const today = new Date()
      let start = null
      if (range.type === 'week') {
        start = subWeeks(today, range.amount)
      } else if (range.type === 'month') {
        start = subMonths(today, range.amount)
      } else if (range.type === 'day') {
        start = subDays(today, range.amount - 1)
      }
      if (!start) start = subDays(today, 29)
      this.dateRange = [toIsoDate(start), toIsoDate(today)]
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    showFull () {
      this.activeQuickRangeId = 'full'
      this.setFullDateRange()
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    setFullDateRange () {
      this.dateRange = getFullTrackingDateRange(this.domainOptions, this.selectedDomainUuids)
      this.maxDate = this.dateRange[1]
    },
    handleDateRangeUpdate (range) {
      this.dateRange = Array.isArray(range) && range.length === 2 ? [...range] : null
      this.activeQuickRangeId = null
      syncTrackingQueryFromVm(this)
      this.scheduleStatsReload()
    },
    onGridReady (params) {
      this.gridApi = params.api
      this.updatePinnedTotals()
      this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
    },
    updatePinnedTotals () {
      if (this.gridApi) {
        this.gridApi.setPinnedBottomRowData(this.tableTotals ? [this.tableTotals] : [])
      }
    },
    syncReadsRingMode () {
      if (!this.readsRingOptions.some(option => option.value === this.readsRingMode)) {
        this.readsRingMode = this.readsRingOptions[0]?.value || 'downloads'
      }
    },
    getSessionSourceCount (source, sessionEntry = null) {
      return Number(this.sourceSessionVolumeMap[`${source}::${sessionEntry || 'null'}`] || 0)
    },
    sumSessionSourceCounts (sources, sessionEntry = null) {
      return (Array.isArray(sources) ? sources : []).reduce((sum, source) => sum + this.getSessionSourceCount(source, sessionEntry), 0)
    },
    getNonNotificationSessionSourceCount (source) {
      return Math.max(0, Number(this.sourceVolumeMap[source] || 0) - this.getSessionSourceCount(source, 'notification'))
    },
    formatDateLabel (value) {
      const parsed = value ? parseISO(value) : null
      if (!parsed || Number.isNaN(parsed.getTime())) {
        return value
      }
      return format(parsed, 'dd/MM', { locale: fr })
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    },
    formatDecimal (value) {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value || 0))
    },
    formatMonthlyValue (value, type) {
      return type === 'decimal' ? this.formatDecimal(value) : this.formatNumber(value)
    },
    formatSourceLabel (source) {
      return SOURCE_LABELS[source] || source
    },
    formatSensitiveDomains (domains) {
      const rows = Array.isArray(domains) ? domains : []
      if (!rows.length) return 'Aucun domaine'
      return rows.slice(0, 3).map(row => row.domain_label).join(', ') + (rows.length > 3 ? ` +${rows.length - 3}` : '')
    },
    hasHiddenSensitiveDomains (term) {
      return Array.isArray(term?.domains) && term.domains.length > 3
    },
    hexToRgba (hex, opacity) {
      const value = String(hex || '').replace('#', '')
      if (value.length !== 6) return `rgba(37, 99, 235, ${opacity})`
      const r = parseInt(value.slice(0, 2), 16)
      const g = parseInt(value.slice(2, 4), 16)
      const b = parseInt(value.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    },
    openReadsMonthlyTracking () {
      this.isReadsMonthlyTrackingOpen = true
      if (!this.isInitializingTracking) {
        this.loadMonthlyTracking()
      }
    },
    closeReadsMonthlyTracking () {
      this.isReadsMonthlyTrackingOpen = false
      if (!this.isSourcesMonthlyTrackingOpen && this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }
    },
    openSourcesMonthlyTracking () {
      this.isSourcesMonthlyTrackingOpen = true
      if (!this.isInitializingTracking) {
        this.loadMonthlyTracking()
      }
    },
    closeSourcesMonthlyTracking () {
      this.isSourcesMonthlyTrackingOpen = false
      if (!this.isReadsMonthlyTrackingOpen && this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }
    },
    openDomainsWithoutInterlocutor () {
      this.isDomainsWithoutInterlocutorOpen = true
    },
    closeDomainsWithoutInterlocutor () {
      this.isDomainsWithoutInterlocutorOpen = false
    },
    openDomainsWithInterlocutorWithoutConversation () {
      this.isDomainsWithInterlocutorWithoutConversationOpen = true
    },
    closeDomainsWithInterlocutorWithoutConversation () {
      this.isDomainsWithInterlocutorWithoutConversationOpen = false
    },
    openSensitiveDomains (term) {
      if (!this.hasHiddenSensitiveDomains(term)) {
        return
      }

      this.selectedSensitiveTerm = term
    },
    closeSensitiveDomains () {
      this.selectedSensitiveTerm = null
    }
  }
}
</script>

<style scoped>
.tracking-filter-field {
  min-width: 11rem;
  flex: 1 1 11rem;
  display: flex;
  align-items: center;
}

.tracking-filter-field--wide {
  min-width: 16rem;
  flex: 1.3 1 16rem;
}

.content-top-grid,
.content-secondary-grid {
  --content-reads-card-height: 31.25rem;
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(340px, 0.75fr);
  gap: 1.5rem;
}

.content-secondary-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.content-space {
  overflow: hidden;
}

.content-space--reads {
  min-height: var(--content-reads-card-height);
}

.content-space--searches {
  display: flex;
  flex-direction: column;
  height: var(--content-reads-card-height);
  max-height: var(--content-reads-card-height);
  min-height: 0;
}

.content-space--source {
  grid-column: 1 / -1;
}

.content-space-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.content-space-header--compact {
  min-height: 5.25rem;
  align-items: center;
}

.content-card-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(150px, 190px);
  gap: 1rem;
  align-items: center;
  padding: 1.25rem;
}

.tracking-chart-panel,
.content-source-chart {
  height: 19rem;
  min-width: 0;
  overflow: hidden;
}

.content-source-chart {
  height: 22rem;
  padding: 1.25rem;
}

.content-source-mix {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(150px, 0.38fr);
  gap: 1.25rem;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.content-source-pie,
.content-source-bars {
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.content-empty-state {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  font-size: 0.875rem;
}

.content-ring-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.content-ring {
  position: relative;
  width: 8rem;
  height: 8rem;
}

.content-ring svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.content-ring-bg,
.content-ring-progress {
  fill: none;
  stroke-width: 12;
}

.content-ring-bg {
  stroke: #e5e7eb;
}

.content-ring-progress {
  stroke: #2563eb;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.25s ease;
}

.content-ring span {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.content-ring-card p,
.content-message-body p {
  margin: 0.5rem 0 0;
  color: #4b5563;
  font-size: 0.875rem;
}

.content-segmented-control {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.85rem;
  border-radius: 9999px;
  border: 1px solid transparent;
  background: #f3f4f6;
  padding: 0.25rem;
}

.content-segmented-control button {
  min-width: 4.5rem;
  border-radius: 9999px;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.45rem 0.7rem;
}

.content-segmented-control button.is-active {
  background: #2563eb;
  color: #ffffff;
}

.content-search-body {
  display: grid;
  gap: 1rem;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1.25rem;
}

.content-search-body h3 {
  margin: 0 0 0.6rem;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
}

.content-list {
  display: grid;
  gap: 0.55rem;
}

.content-list-row,
.content-list-empty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f9fafb;
  padding: 0.7rem 0.85rem;
}

.content-list-row--button {
  width: 100%;
  color: inherit;
  font: inherit;
  text-align: left;
}

.content-list-row--button:disabled {
  cursor: default;
  opacity: 1;
}

.content-list-row--button.is-clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.content-list-row--button.is-clickable:hover {
  border-color: #bfdbfe;
  background: #eff6ff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.content-list-row strong {
  display: block;
  color: #111827;
  font-size: 0.875rem;
}

.content-list-row span,
.content-list-empty {
  color: #4b5563;
  font-size: 0.75rem;
}

.content-list-row small {
  display: block;
  margin-top: 0.2rem;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
}

.content-list-row em {
  flex: 0 0 auto;
  color: #111827;
  font-style: normal;
  font-weight: 700;
}

.content-message-body {
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
}

.content-message-entry {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}

.content-message-entry:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.content-link-button {
  color: #2563eb;
  font-size: 0.875rem;
  font-weight: 600;
}

.content-link-button:hover {
  color: #1d4ed8;
}

.content-banner-table-wrapper {
  padding: 1.25rem;
}

.content-simple-table {
  width: 100%;
  border-collapse: collapse;
}

.content-simple-table th,
.content-simple-table td {
  padding: 0.75rem 0.65rem;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
  font-size: 0.875rem;
}

.content-simple-table th {
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: left;
  text-transform: uppercase;
  background: #f9fafb;
}

.is-right {
  text-align: right;
}

@media (max-width: 1180px) {
  .content-top-grid,
  .content-secondary-grid,
  .content-card-body {
    grid-template-columns: 1fr;
  }

  .content-space--reads {
    min-height: 0;
  }

  .content-space--searches {
    height: auto;
    max-height: none;
  }

  .content-source-chart {
    height: 34rem;
  }

  .content-source-mix {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .content-space-header,
  .tracking-modal-header {
    flex-direction: column;
  }

  .tracking-period-stats {
    justify-content: flex-start;
    width: 100%;
  }

  .tracking-period-stat {
    flex: 1 1 8rem;
  }

  .tracking-modal-domain-filter {
    width: 100%;
    margin-left: 0;
  }

  .content-source-chart {
    height: 36rem;
  }
}
</style>
